import React, { useCallback, useEffect, useLayoutEffect, useRef, useState, type VideoHTMLAttributes } from 'react';

import { type ArtifactType } from '@app/client/common/structs/nft/artifacts';

import './Video.scss';

interface VideoProps {
    artifactType: ArtifactType;
    active: boolean;
    className?: string;
    muted: boolean;
    videoUrl?: string;
    posterUrl?: string;
}

type Props = VideoProps & VideoHTMLAttributes<HTMLVideoElement>;

export function Video({ className = '', active, muted = true, artifactType, videoUrl, posterUrl }: Props) {
    const [canPlay, setCanPlay] = useState(false);
    const [isPosterLoaded, setIsPosterLoaded] = useState(false);
    const [isVideoPlaying, setIsVideoPlaying] = useState(false);
    const videoRef = useRef<HTMLVideoElement>(null);
    const baseUrl = videoUrl?.replace(/\.[^.]+$/, '');

    const handlePlay = useCallback(() => {
        const video = videoRef.current;
        if (video && !isVideoPlaying) {
            const playPromise = video.play();
            if (playPromise !== undefined) {
                playPromise
                    .then(() => {
                        return video.play();
                    })
                    .catch((error) => {
                        // eslint-disable-next-line no-console
                        console.warn(error);
                        return video.pause();
                    });
            }
        }
    }, [isVideoPlaying]);

    useEffect(() => {
        if (active) {
            handlePlay();
        }
    }, [active, handlePlay]);

    useEffect(() => {
        if (!active && isVideoPlaying) {
            videoRef?.current?.pause();
        }
    }, [active, isVideoPlaying]);

    useLayoutEffect(() => {
        const video = videoRef?.current;

        if (video) {
            const isPlaying = Boolean(
                video &&
                    video.currentTime > 0 &&
                    !video.paused &&
                    !video.ended &&
                    video.readyState > video.HAVE_CURRENT_DATA,
            );
            if (active) {
                handlePlay();
            }
            setIsVideoPlaying(isPlaying);
        }
    }, [active, handlePlay, setIsVideoPlaying]);

    const _className = `yat-artifacts__video
    ${canPlay ? 'yat-artifacts__video--can-play' : ''}
    ${isPosterLoaded ? 'yat-artifacts__video--poster-loaded' : ''}
    ${className}`;

    return (
        <div className={_className}>
            <img
                className="yat-artifacts__video__poster"
                src={posterUrl}
                onLoad={() => setIsPosterLoaded(true)}
                alt={`${artifactType}-poster`}
            />
            <video
                className="yat-artifacts__video__video"
                webkit-playsinline="true"
                controls={false}
                ref={videoRef}
                muted={muted}
                playsInline={true}
                loop={true}
                onCanPlay={() => setCanPlay(true)}
            >
                <source src={`${baseUrl}.mp4`} type="video/mp4" />
                <source src={`${baseUrl}.webm`} type="video/webm" />
            </video>
        </div>
    );
}
